/**
 * Sprouties Subscription Widget - FINAL v3
 * * FIXES:
 * - Removed restriction that hid savings for subscription-only products.
 * - Robust logic to apply the hardcoded formula (65 * deliveries * %) whenever matches are found.
 */

(function () {
  "use strict";

  const SELECTORS = {
    widget: ".sprouties-subscription-widget",
    card: ".ssw-card",
    subscribeCard: ".ssw-card--subscribe",
    onetimeCard: ".ssw-card--onetime",
    selectedClass: "ssw-card--selected",
    frequencyBtn: ".ssw-frequency__btn",
    frequencyActiveClass: "ssw-frequency__btn--active",
    sellingPlanInput: '[data-selling-plan-input]',
    subscribePrice: "[data-subscribe-price]",
    onetimePrice: "[data-onetime-price]",
    savingsPercent: "[data-savings-percent]",
    savingsAmount: "[data-savings-amount]",
    savingsRow: ".ssw-savings-row",
    radioInput: 'input[type="radio"]',
  };

  function init() {
    document.querySelectorAll(SELECTORS.widget).forEach(setupWidget);
  }

  function setupWidget(widget) {
    if (widget.dataset.initialized === "true") return;
    widget.dataset.initialized = "true";

    const cards = widget.querySelectorAll(SELECTORS.card);
    const frequencyBtns = widget.querySelectorAll(SELECTORS.frequencyBtn);

    // Card selection (Subscribe vs One-time)
    cards.forEach((card) => {
      card.addEventListener("click", function (e) {
        if (e.target.closest(SELECTORS.frequencyBtn)) return;
        handleCardSelection(widget, this, cards);
      });

      const radio = card.querySelector(SELECTORS.radioInput);
      if (radio) {
        radio.addEventListener("change", function () {
          if (this.checked) handleCardSelection(widget, card, cards);
        });
      }
    });

    // Frequency button selection
    frequencyBtns.forEach((btn) => {
      btn.addEventListener("click", function () {
        handleFrequencySelection(widget, this, frequencyBtns);
      });
    });

    // Initialize displays on load
    const activeFreqBtn = widget.querySelector(
      `${SELECTORS.frequencyBtn}.${SELECTORS.frequencyActiveClass}`
    );
    if (activeFreqBtn) {
      updatePriceDisplay(widget, activeFreqBtn);
      updateSavingsDisplay(widget, activeFreqBtn);

      // Set initial selling_plan value
      const isSubscribe = isSubscribeSelected(widget);
      if (isSubscribe) {
        setSellingPlanValue(widget, activeFreqBtn.dataset.planId);
        setSellingPlanEnabled(widget, true);
      } else {
        setSellingPlanEnabled(widget, false);
      }
    }

    // Listen for variant changes (Shopify theme event)
    document.addEventListener("variant:change", function (e) {
      handleVariantChange(widget, e.detail);
    });
  }

  // ─────────────────────────────────────────────────────────────
  // CARD SELECTION (Subscribe vs One-time)
  // ─────────────────────────────────────────────────────────────
  function handleCardSelection(widget, selectedCard, allCards) {
    allCards.forEach((c) => c.classList.remove(SELECTORS.selectedClass));
    selectedCard.classList.add(SELECTORS.selectedClass);

    const radio = selectedCard.querySelector(SELECTORS.radioInput);
    if (radio) radio.checked = true;

    const isSubscribe = selectedCard.dataset.purchaseType === "subscribe";
    
    // Check if we need to show savings row (only if savings exist)
    if (isSubscribe) {
        const activeBtn = widget.querySelector(
          `${SELECTORS.frequencyBtn}.${SELECTORS.frequencyActiveClass}`
        );
        updateSavingsDisplay(widget, activeBtn); 
    } else {
        const savingsRow = widget.querySelector(SELECTORS.savingsRow);
        if (savingsRow) savingsRow.style.display = "none";
    }

    if (isSubscribe) {
      const activeBtn = widget.querySelector(
        `${SELECTORS.frequencyBtn}.${SELECTORS.frequencyActiveClass}`
      );
      if (activeBtn) {
        setSellingPlanValue(widget, activeBtn.dataset.planId);
      }
      setSellingPlanEnabled(widget, true);
    } else {
      setSellingPlanEnabled(widget, false);
    }

    dispatchWidgetEvent(widget, "purchaseTypeChange", {
      type: selectedCard.dataset.purchaseType,
    });
  }

  // ─────────────────────────────────────────────────────────────
  // FREQUENCY SELECTION
  // ─────────────────────────────────────────────────────────────
  function handleFrequencySelection(widget, selectedBtn, allBtns) {
    allBtns.forEach((b) => b.classList.remove(SELECTORS.frequencyActiveClass));
    selectedBtn.classList.add(SELECTORS.frequencyActiveClass);

    updatePriceDisplay(widget, selectedBtn);
    updateSavingsDisplay(widget, selectedBtn);

    if (isSubscribeSelected(widget)) {
      setSellingPlanValue(widget, selectedBtn.dataset.planId);
      setSellingPlanEnabled(widget, true);
    }

    dispatchWidgetEvent(widget, "frequencyChange", {
      planId: selectedBtn.dataset.planId,
      planPrice: selectedBtn.dataset.planPrice,
    });
  }

  // ─────────────────────────────────────────────────────────────
  // SELLING PLAN INPUT MANAGEMENT
  // ─────────────────────────────────────────────────────────────
  function getProductForm(widget) {
    return widget.closest("form") || widget.closest(".product-form")?.querySelector("form");
  }

  function getAllSellingPlanInputs(widget) {
    const form = getProductForm(widget);
    if (!form) {
      const input = widget.querySelector(SELECTORS.sellingPlanInput);
      return input ? [input] : [];
    }
    return Array.from(form.querySelectorAll('input[name="selling_plan"]'));
  }

  function setSellingPlanValue(widget, value) {
    getAllSellingPlanInputs(widget).forEach((input) => {
      input.value = value || "";
    });
  }

  function setSellingPlanEnabled(widget, enabled) {
    getAllSellingPlanInputs(widget).forEach((input) => {
      input.disabled = !enabled;
    });
  }

  function isSubscribeSelected(widget) {
    const subscribeCard = widget.querySelector(SELECTORS.subscribeCard);
    return subscribeCard && subscribeCard.classList.contains(SELECTORS.selectedClass);
  }

  // ─────────────────────────────────────────────────────────────
  // PRICE DISPLAY
  // ─────────────────────────────────────────────────────────────
  function getSubscriptionMonthlyCents(widget, freqBtn) {
    if (!freqBtn) return 0;
    const monthlyPrice = parseInt(freqBtn.dataset.planPrice, 10);
    if (isNaN(monthlyPrice) || monthlyPrice <= 0) return 0;
    return monthlyPrice;
  }

  function updatePriceDisplay(widget, freqBtn) {
    const priceEl = widget.querySelector(SELECTORS.subscribePrice);
    if (!priceEl || !freqBtn) return;

    const monthly = getSubscriptionMonthlyCents(widget, freqBtn);
    if (monthly > 0) {
      priceEl.innerHTML =
        formatMoney(widget, monthly) + '<span class="ssw-price-period">/month</span>';
    }
  }

  // ─────────────────────────────────────────────────────────────
  // SAVINGS DISPLAY (CORE LOGIC FIX)
  // ─────────────────────────────────────────────────────────────
  function updateSavingsDisplay(widget, freqBtn) {
    const percentEl = widget.querySelector(SELECTORS.savingsPercent);
    const amountEl = widget.querySelector(SELECTORS.savingsAmount);
    const savingsRow = widget.querySelector(SELECTORS.savingsRow);
    
    if (!percentEl || !amountEl || !freqBtn || !savingsRow) return;

    // NOTE: Removed "isSubscriptionOnly" check here. 
    // We want to show savings based on the formula even if there is no one-time purchase option.

    const isSubscribe = isSubscribeSelected(widget);
    const planLabel = freqBtn.textContent.toLowerCase().trim();
    
    // --- SPECIAL FORMULA LOGIC ---
    // Total savings = (delivered retail × deliveries) - subscription price
    // Delivered retail = AED 69 box + AED 10 delivery = AED 79
    // Discount % shown is against delivered retail total

    const DELIVERED_RETAIL_PRICE = 79;
    let deliveries = 0;
    let monthlySubPrice = 0;
    let useSpecialFormula = false;

    if (planLabel.includes('weekly') || planLabel.includes('7 day') || planLabel.includes('7-day')) {
      deliveries = 4;
      monthlySubPrice = 235;
      useSpecialFormula = true;
    } else if (planLabel.includes('2 week') || planLabel.includes('14 day') || planLabel.includes('14-day')) {
      deliveries = 2;
      monthlySubPrice = 138;
      useSpecialFormula = true;
    } else if (planLabel.includes('10 day') || planLabel.includes('10-day')) {
      deliveries = 3;
      monthlySubPrice = 195;
      useSpecialFormula = true;
    }

    if (useSpecialFormula) {
      const totalRetailValue = DELIVERED_RETAIL_PRICE * deliveries;
      const savingsAmount = totalRetailValue - monthlySubPrice;
      const discountPercent = Math.round((savingsAmount / totalRetailValue) * 100);

      const savingsAmountCents = savingsAmount * 100;

      percentEl.textContent = discountPercent + "%";
      amountEl.textContent = formatMoney(widget, savingsAmountCents);

      if (isSubscribe) {
        savingsRow.style.display = "";
      } else {
        savingsRow.style.display = "none";
      }
      return;
    }
    // --- END SPECIAL FORMULA ---

    // Fallback: Standard Logic (Original - Subscription)
    const originalPrice = parseInt(widget.dataset.originalPrice, 10) || 0;
    const subscriptionPrice = parseInt(freqBtn.dataset.planPrice, 10) || 0;

    if (originalPrice > 0 && subscriptionPrice > 0 && subscriptionPrice < originalPrice) {
      const savingsAmount = originalPrice - subscriptionPrice;
      const savingsPercent = Math.round((savingsAmount / originalPrice) * 100);

      percentEl.textContent = savingsPercent + "%";
      amountEl.textContent = formatMoney(widget, savingsAmount);
      
      if (isSubscribe) {
        savingsRow.style.display = ""; 
      }
    } else {
      // Hide if no savings
      savingsRow.style.display = "none";
      percentEl.textContent = "0%";
      amountEl.textContent = formatMoney(widget, 0);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // VARIANT CHANGE HANDLING
  // ─────────────────────────────────────────────────────────────
  function handleVariantChange(widget, detail) {
    if (!detail || !detail.variant) return;

    const variant = detail.variant;
    widget.dataset.variantId = variant.id;
    widget.dataset.originalPrice = variant.price;

    const onetimePriceEl = widget.querySelector(SELECTORS.onetimePrice);
    if (onetimePriceEl) {
      onetimePriceEl.textContent = formatMoney(widget, variant.price);
    }

    if (detail.selling_plan_allocations) {
      const frequencyBtns = widget.querySelectorAll(SELECTORS.frequencyBtn);
      frequencyBtns.forEach((btn) => {
        const planId = btn.dataset.planId;
        const allocation = detail.selling_plan_allocations.find(
          (a) => String(a.selling_plan_id) === String(planId)
        );
        if (allocation) {
          btn.dataset.planPrice = allocation.price;
          btn.dataset.planComparePrice = allocation.compare_at_price || variant.price;
        }
      });
    }

    const activeBtn = widget.querySelector(
      `${SELECTORS.frequencyBtn}.${SELECTORS.frequencyActiveClass}`
    );
    if (activeBtn) {
      updatePriceDisplay(widget, activeBtn);
      updateSavingsDisplay(widget, activeBtn);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // UTILITIES
  // ─────────────────────────────────────────────────────────────
  function formatMoney(widget, cents) {
    if (typeof cents !== "number" || isNaN(cents)) cents = 0;
    const amount = (cents / 100).toFixed(0); 
    
    const currency = widget.dataset.currencySymbol || "AED";
    
    return `${currency} ${amount}`;
  }

  function dispatchWidgetEvent(widget, eventName, detail) {
    widget.dispatchEvent(
      new CustomEvent("ssw:" + eventName, {
        bubbles: true,
        detail: detail,
      })
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          if (node.matches && node.matches(SELECTORS.widget)) {
            setupWidget(node);
          }
          const nested = node.querySelectorAll
            ? node.querySelectorAll(SELECTORS.widget)
            : [];
          nested.forEach(setupWidget);
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();